import {createContext, useCallback, useEffect, useState} from "react";
import log from "loglevel";
import {
  DataChangeEntityType,
  useEnableDataChangedEventsMutation,
  useOnDataChangedSubscription
} from "../../../generated/graphql";
import {debounce} from "lodash";
import {Constants} from "../../../constants/constants";

type ManifestDetailsChangeState = {
  entityId?: string;
  entityType?: string;
  updated?: number;
};

const ManifestDetailsChangeContext = createContext<ManifestDetailsChangeState | undefined>({
  entityId: undefined,
  entityType: undefined,
  updated: undefined
});

type ManifestDetailsChangeEventProviderProps = {
  children: JSX.Element[] | JSX.Element;
  manifestDriverId: number;
};

const ManifestDetailsChangeEventProvider = ({manifestDriverId, children}: ManifestDetailsChangeEventProviderProps) => {
  const [state, setState] = useState<ManifestDetailsChangeState>();

  const [enableDataChangedEvents] = useEnableDataChangedEventsMutation();

  const {data, loading, error} = useOnDataChangedSubscription({
    variables: {
      entityType: DataChangeEntityType.Manifest,
      entityId: manifestDriverId.toString()
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdate = useCallback(
    debounce(
      (data, loading, error) => {
        if (error) {
          log.error("Error in data change subscription.", error, error.graphQLErrors);
        }

        if (!loading && data) {
          setState({
            ...data.onDataChanged,
            updated: Date.now()
          } as ManifestDetailsChangeState);
          log.debug("Data change propagated.", data.onDataChanged);
        }
      },
      2000,
      {maxWait: 5000}
    ),
    [manifestDriverId]
  );

  useEffect(() => {
    const setupSubscription = async () => {
      const result = await enableDataChangedEvents({
        variables: {
          entityType: DataChangeEntityType.Manifest,
          entityId: manifestDriverId.toString(),
          ttl: Constants.DATA_CHANGED_EVENTS_TTL / 1000
        }
      });

      if (result.errors) {
        log.error("Encountered error(s) enabling data change events for %s: %s", manifestDriverId, result.errors);
      } else {
        log.debug("Enabled data change events for %s", manifestDriverId);
      }
    };
    setupSubscription();
    const interval = setInterval(() => {
      setupSubscription();
    }, Constants.DATA_CHANGED_EVENTS_TTL);

    return () => clearInterval(interval);
  }, [enableDataChangedEvents, manifestDriverId]);

  useEffect(() => {
    debouncedUpdate(data, loading, error);
    log.debug("Data change debounced.");
  }, [data, loading, error, debouncedUpdate]);

  return <ManifestDetailsChangeContext.Provider value={state}>{children}</ManifestDetailsChangeContext.Provider>;
};

export type {ManifestDetailsChangeState};
export default ManifestDetailsChangeEventProvider;
export {ManifestDetailsChangeContext};
